import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  AlertTriangle, RefreshCw, Loader2, TrendingDown, History, CheckCircle2,
} from 'lucide-react';
import { FiltrosPrestamos, InformeButtons } from './_prestamosUtils';

const DATE_FIELDS = [
  { value: '', label: 'Sin filtro de fecha' },
  { value: 'mora', label: 'Fecha de mora' },
  { value: 'solicitud', label: 'Fecha de solicitud' },
  { value: 'aprobacion', label: 'Fecha de aprob./rechazo' },
  { value: 'vencimiento', label: 'Vencimiento de cuota' },
];

const fmtMoney = (n) =>
  (n || n === 0)
    ? new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n)
    : '—';

const StatCard = ({ label, value, sub, testid, accent }) => (
  <div className={`rounded-xl border p-4 ${accent || 'border-slate-200 dark:border-slate-800'}`} data-testid={testid}>
    <p className="text-xs text-slate-500">{label}</p>
    <p className="text-xl font-bold text-slate-900 dark:text-white mt-0.5">{value}</p>
    {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

const ClienteCell = ({ it }) => (
  <td className="px-3 py-2.5">
    <p className="font-medium text-slate-800 dark:text-slate-100">{it.nombre_completo || '—'}</p>
    <p className="font-mono text-xs text-slate-500">{it.cuit}</p>
  </td>
);

// Tabla de MORA ACTIVA (préstamos con cuotas vencidas impagas hoy)
const TablaMoraActiva = ({ items }) => (
  <Card className="border-slate-200 dark:border-slate-800 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500">
          <tr>
            <th className="text-left px-3 py-2.5 font-medium">Cliente</th>
            <th className="text-left px-3 py-2.5 font-medium">Teléfono</th>
            <th className="text-left px-3 py-2.5 font-medium">Frec.</th>
            <th className="text-right px-3 py-2.5 font-medium">Cap. prestado</th>
            <th className="text-right px-3 py-2.5 font-medium">Valor cuota</th>
            <th className="text-center px-3 py-2.5 font-medium">Venc./Tot.</th>
            <th className="text-center px-3 py-2.5 font-medium">Días mora</th>
            <th className="text-right px-3 py-2.5 font-medium">Total mora</th>
            <th className="text-right px-3 py-2.5 font-medium">→30d</th>
            <th className="text-right px-3 py-2.5 font-medium">→60d</th>
            <th className="text-right px-3 py-2.5 font-medium">→90d</th>
            <th className="text-right px-3 py-2.5 font-medium">Recupero</th>
            <th className="text-left px-3 py-2.5 font-medium">Próx. cuota</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.waya_id} className="border-t border-slate-100 dark:border-slate-800 bg-rose-50/40 dark:bg-rose-950/10" data-testid={`mora-row-${it.waya_id}`}>
              <ClienteCell it={it} />
              <td className="px-3 py-2.5 font-mono text-xs text-slate-600 dark:text-slate-300" data-testid={`mora-tel-${it.waya_id}`}>{it.telefono || '—'}</td>
              <td className="px-3 py-2.5 text-slate-600 dark:text-slate-300">{it.frecuencia || '—'}</td>
              <td className="px-3 py-2.5 text-right text-slate-700 dark:text-slate-200">{fmtMoney(it.monto_solicitado)}</td>
              <td className="px-3 py-2.5 text-right text-slate-600 dark:text-slate-300">{fmtMoney(it.valor_cuota)}</td>
              <td className="px-3 py-2.5 text-center text-slate-600 dark:text-slate-300">{it.cuotas_vencidas}/{it.cuotas_totales}</td>
              <td className="px-3 py-2.5 text-center">
                <Badge className="bg-rose-100 text-rose-700 border-0" data-testid={`mora-dias-${it.waya_id}`}>{it.dias_mora}</Badge>
              </td>
              <td className="px-3 py-2.5 text-right font-semibold text-rose-600" data-testid={`mora-total-${it.waya_id}`}>{fmtMoney(it.total_mora)}</td>
              <td className="px-3 py-2.5 text-right text-slate-500">{it.monto_futuro_30d ? fmtMoney(it.monto_futuro_30d) : '—'}</td>
              <td className="px-3 py-2.5 text-right text-slate-500">{it.monto_futuro_60d ? fmtMoney(it.monto_futuro_60d) : '—'}</td>
              <td className="px-3 py-2.5 text-right text-slate-500">{it.monto_futuro_90d ? fmtMoney(it.monto_futuro_90d) : '—'}</td>
              <td className="px-3 py-2.5 text-right text-slate-600 dark:text-slate-300">{fmtMoney(it.recupero_esperado)}</td>
              <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{it.proxima_cuota_fvto || 'vencidas'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Card>
);

// Tabla de HISTORIAL de mora (estuvo en mora / mora paga): foco en atrasos ya regularizados
const TablaMoraHistorica = ({ items, prefix, mostrarRecupero }) => (
  <Card className="border-slate-200 dark:border-slate-800 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500">
          <tr>
            <th className="text-left px-3 py-2.5 font-medium">Cliente</th>
            <th className="text-left px-3 py-2.5 font-medium">Teléfono</th>
            <th className="text-left px-3 py-2.5 font-medium">Frec.</th>
            <th className="text-left px-3 py-2.5 font-medium">Estado</th>
            <th className="text-right px-3 py-2.5 font-medium">Cap. prestado</th>
            <th className="text-right px-3 py-2.5 font-medium">Valor cuota</th>
            <th className="text-center px-3 py-2.5 font-medium">Cuotas (pag./tot.)</th>
            <th className="text-center px-3 py-2.5 font-medium">Pagadas tarde</th>
            <th className="text-center px-3 py-2.5 font-medium">Máx. atraso</th>
            {mostrarRecupero && <th className="text-right px-3 py-2.5 font-medium">Recupero</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.waya_id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`${prefix}-row-${it.waya_id}`}>
              <ClienteCell it={it} />
              <td className="px-3 py-2.5 font-mono text-xs text-slate-600 dark:text-slate-300" data-testid={`${prefix}-tel-${it.waya_id}`}>{it.telefono || '—'}</td>
              <td className="px-3 py-2.5 text-slate-600 dark:text-slate-300">{it.frecuencia || '—'}</td>
              <td className="px-3 py-2.5 text-slate-600 dark:text-slate-300">{it.estado || '—'}</td>
              <td className="px-3 py-2.5 text-right text-slate-700 dark:text-slate-200">{fmtMoney(it.monto_solicitado)}</td>
              <td className="px-3 py-2.5 text-right text-slate-600 dark:text-slate-300">{fmtMoney(it.valor_cuota)}</td>
              <td className="px-3 py-2.5 text-center text-slate-600 dark:text-slate-300">{it.cuotas_pagadas}/{it.cuotas_totales}</td>
              <td className="px-3 py-2.5 text-center">
                <Badge className="bg-amber-100 text-amber-700 border-0">{it.cuotas_pagadas_tarde}</Badge>
              </td>
              <td className="px-3 py-2.5 text-center">
                {it.max_atraso_dias > 0
                  ? <Badge className="bg-orange-100 text-orange-700 border-0" data-testid={`${prefix}-atraso-${it.waya_id}`}>{it.max_atraso_dias} d</Badge>
                  : <span className="text-slate-400">—</span>}
              </td>
              {mostrarRecupero && <td className="px-3 py-2.5 text-right text-slate-600 dark:text-slate-300">{fmtMoney(it.recupero_esperado)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Card>
);

const EmptyState = ({ testid, texto }) => (
  <div className="text-center text-slate-400 py-16" data-testid={testid}>
    <TrendingDown className="h-10 w-10 mx-auto mb-3 opacity-40" />
    {texto}
  </div>
);

const MoraConsolidada = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalc, setRecalc] = useState(false);
  const [tab, setTab] = useState('en-mora');
  const [filtros, setFiltros] = useState({ date_field: '', desde: '', hasta: '', estado: '', frecuencia: '', q: '' });
  const pollRef = useRef(null);

  const load = useCallback(async (f) => {
    setLoading(true);
    try {
      const r = await api.get('/motor/mora', { params: f || filtros });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo calcular la mora');
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => {
    load(filtros);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []); // eslint-disable-line

  const recalcular = async () => {
    setRecalc(true);
    try {
      await api.post('/kpis/automatizaciones/prestamos-detalle-mora/ejecutar');
      toast.info('Recalculando: bajando el detalle de cuotas desde Waya…');
      let intentos = 0;
      pollRef.current = setInterval(async () => {
        intentos += 1;
        if (intentos > 50) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setRecalc(false);
          toast.error('El recálculo está tardando demasiado. Probá "Recalcular" de nuevo en unos minutos.');
          load(filtros);
          return;
        }
        try {
          const r = await api.get('/kpis/prestamos/detalle');
          const st = r.data?.scrape?.status;
          if (st && st !== 'en_ejecucion') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setRecalc(false);
            if (st === 'error') toast.error('El recálculo falló al sincronizar con Waya');
            else toast.success('Detalle actualizado. Recalculando mora…');
            load(filtros);
          }
        } catch (e) { /* seguir poll */ }
      }, 6000);
    } catch (e) {
      setRecalc(false);
      toast.error(e?.response?.data?.detail || 'No se pudo iniciar el recálculo');
    }
  };

  const items = data?.items || [];
  const estuvo = data?.estuvo_en_mora || [];
  const paga = data?.mora_paga || [];
  const frec = data?.por_frecuencia || {};

  return (
    <div className="p-6 lg:p-8 pt-20 sm:pt-6 lg:pt-8 max-w-7xl mx-auto" data-testid="mora-consolidada">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-rose-500/10"><AlertTriangle className="h-6 w-6 text-rose-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Mora Consolidada</h1>
            <p className="text-sm text-slate-500">
              Cálculo local sobre las cuotas sincronizadas{data?.hoy ? ` · al ${data.hoy}` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <InformeButtons tipo="mora" params={filtros} />
          <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white" onClick={recalcular} disabled={recalc} data-testid="mora-recalcular-btn">
            {recalc ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {recalc ? 'Recalculando…' : 'Recalcular (Waya)'}
          </Button>
        </div>
      </div>

      <FiltrosPrestamos value={filtros} onChange={setFiltros} onApply={(f) => load(f)} dateFields={DATE_FIELDS} />

      {loading ? (
        <div className="text-center text-slate-500 py-16"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : !data ? (
        <div className="text-center text-slate-400 py-16" data-testid="mora-sin-datos">
          <AlertTriangle className="h-10 w-10 mx-auto mb-3 opacity-40" />
          No se pudo cargar la mora. Verificá tus permisos o reintentá.
        </div>
      ) : (
        <>
          {/* Agregados globales (mora activa) */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard label="Mora total (activa)" value={fmtMoney(data.total_mora)} sub={`Capital ${fmtMoney(data.total_capital)} + Punit. ${fmtMoney(data.total_punitorios)}`} testid="mora-total" accent="border-rose-200 bg-rose-50/40 dark:bg-rose-950/10" />
            <StatCard label="Préstamos en mora" value={`${data.prestamos_con_mora} / ${data.total_prestamos}`} sub={`Tasa ${data.tasa_mora_global}% · ${data.prestamos_activos} activos`} testid="mora-tasa" />
            <StatCard label="Recupero esperado" value={fmtMoney(data.total_recupero_esperado)} sub="Cuotas restantes × valor cuota" testid="mora-recupero" />
            <StatCard label="Por vencer 30/60/90" value={fmtMoney(data.total_mora_futura_30d)} sub={`60d ${fmtMoney(data.total_mora_futura_60d)} · 90d ${fmtMoney(data.total_mora_futura_90d)}`} testid="mora-proyeccion" />
          </div>

          {/* Segmentación por frecuencia (solo mora activa) */}
          {Object.keys(frec).length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="mora-por-frecuencia">
              {Object.entries(frec).map(([f, s]) => (
                <Card key={f} className="p-3 border-slate-200 dark:border-slate-800" data-testid={`mora-frec-${f}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{f}</span>
                    <Badge className="bg-slate-100 text-slate-600 border-0">{s.count}</Badge>
                  </div>
                  <p className="text-lg font-bold text-rose-600 mt-1">{fmtMoney(s.total_mora)}</p>
                  <p className="text-[11px] text-slate-400">{s.prestamos_con_mora} en mora · {s.tasa_mora}%</p>
                  <p className="text-[11px] text-slate-400">Recupero {fmtMoney(s.recupero_esperado)}</p>
                </Card>
              ))}
            </div>
          )}

          {/* Tabs: En mora / Estuvo en mora / Mora paga */}
          <Tabs value={tab} onValueChange={setTab} className="w-full" data-testid="mora-tabs">
            <TabsList className="mb-4" data-testid="mora-tabs-list">
              <TabsTrigger value="en-mora" data-testid="mora-tab-en-mora">
                <AlertTriangle className="h-4 w-4 mr-1.5 text-rose-500" />
                En mora <span className="ml-1.5 text-xs opacity-70">({data.count_en_mora})</span>
              </TabsTrigger>
              <TabsTrigger value="estuvo" data-testid="mora-tab-estuvo">
                <History className="h-4 w-4 mr-1.5 text-amber-500" />
                Estuvo en mora <span className="ml-1.5 text-xs opacity-70">({data.count_estuvo_en_mora})</span>
              </TabsTrigger>
              <TabsTrigger value="paga" data-testid="mora-tab-paga">
                <CheckCircle2 className="h-4 w-4 mr-1.5 text-emerald-500" />
                Mora paga <span className="ml-1.5 text-xs opacity-70">({data.count_mora_paga})</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="en-mora" data-testid="mora-content-en-mora">
              <p className="text-xs text-slate-500 mb-2">Préstamos con cuotas vencidas impagas al día de hoy.</p>
              {items.length === 0
                ? <EmptyState testid="mora-empty" texto={'No hay préstamos en mora activa. Usá "Recalcular (Waya)" si esperabas datos.'} />
                : <TablaMoraActiva items={items} />}
            </TabsContent>

            <TabsContent value="estuvo" data-testid="mora-content-estuvo">
              <p className="text-xs text-slate-500 mb-2">Préstamos <strong>activos</strong> que hoy no están en mora, pero pagaron alguna cuota <strong>después</strong> del vencimiento (se regularizaron).</p>
              {estuvo.length === 0
                ? <EmptyState testid="estuvo-empty" texto="No hay préstamos activos que hayan estado en mora." />
                : <TablaMoraHistorica items={estuvo} prefix="estuvo" mostrarRecupero />}
            </TabsContent>

            <TabsContent value="paga" data-testid="mora-content-paga">
              <p className="text-xs text-slate-500 mb-2">Préstamos <strong>finalizados</strong> (sin cuotas pendientes) que en algún momento estuvieron en mora y terminaron de pagar.</p>
              {paga.length === 0
                ? <EmptyState testid="paga-empty" texto="No hay préstamos finalizados que hayan estado en mora." />
                : <TablaMoraHistorica items={paga} prefix="paga" mostrarRecupero={false} />}
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default MoraConsolidada;
